export const SITE_URL = (
  process.env.REACT_APP_SITE_URL || "https://harbrflow.com"
).replace(/\/$/, "");

export const SITE_NAME = "harbrflow";
export const DEFAULT_TITLE = "harbrflow — AI Automation for Shipping Operations";
export const DEFAULT_DESCRIPTION =
  "harbrflow helps shipping and logistics companies eliminate repetitive operational work through practical AI automation. Book a free automation assessment.";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;
export const TWITTER_HANDLE = "@harbrflow";

/** @type {Record<string, { title: string; description: string; path: string; noindex?: boolean }>} */
export const ROUTE_SEO = {
  "/": {
    path: "/",
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  "/solutions": {
    path: "/solutions",
    title: "AI Automation Solutions for Shipping & Logistics | harbrflow",
    description:
      "Email, document, voice and workflow automation built for freight forwarders, shipping companies, ship management and logistics providers.",
  },
  "/approach": {
    path: "/approach",
    title: "Our Approach — Automate One Workflow, Then Scale | harbrflow",
    description:
      "A low-risk method for shipping operations: automate one workflow first, prove measurable value, then scale inside your existing systems.",
  },
  "/insights": {
    path: "/insights",
    title: "Insights on Shipping Operations & AI Automation | harbrflow",
    description:
      "Practical thinking on efficiency, manual work and the future of operations in shipping and logistics.",
  },
  "/about": {
    path: "/about",
    title: "About harbrflow — AI That Removes Repetitive Work",
    description:
      "harbrflow is an AI automation partner for shipping and logistics. We remove repetitive operational work so teams can focus on judgement and relationships.",
  },
  "/contact": {
    path: "/contact",
    title: "Book a Free AI Automation Assessment | harbrflow",
    description:
      "Tell us about your shipping or logistics operation. We'll identify repetitive workflows and where AI can create measurable value — no obligation.",
  },
  "/privacy": {
    path: "/privacy",
    title: "Privacy Policy | harbrflow",
    description:
      "How harbrflow collects, uses and protects the information you share when requesting an automation assessment.",
  },
};

export const NOT_FOUND_SEO = {
  path: "",
  title: "Page Not Found | harbrflow",
  description: "The page you are looking for has been moved or no longer exists.",
  noindex: true,
};

export function getRouteSeo(pathname) {
  return ROUTE_SEO[pathname] || NOT_FOUND_SEO;
}

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/app-icon-512.png`,
    email: "support@harbrflow.com",
    description: DEFAULT_DESCRIPTION,
    sameAs: [],
    areaServed: "Worldwide",
    knowsAbout: [
      "Shipping automation",
      "Logistics AI",
      "Document automation",
      "Workflow automation",
      "Freight operations",
    ],
  };
}

export function websiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    description: DEFAULT_DESCRIPTION,
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
    },
  };
}

export function webPageJsonLd({ title, description, path }) {
  const url = `${SITE_URL}${path || ""}`;
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url,
    isPartOf: {
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_URL,
    },
  };
}

export function professionalServiceJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: SITE_NAME,
    url: SITE_URL,
    image: DEFAULT_OG_IMAGE,
    email: "support@harbrflow.com",
    description: DEFAULT_DESCRIPTION,
    serviceType: "AI automation for shipping and logistics",
    areaServed: "Worldwide",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Automation capabilities",
      itemListElement: [
        "Email Automation",
        "Document Automation",
        "Voice AI",
        "Workflow Automation",
        "Customer Communication",
        "AI Integrations",
        "Custom AI Solutions",
      ].map((name) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name,
        },
      })),
    },
  };
}
